/** @format */

import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ComponentRef, OnDestroy, TemplateRef, inject, viewChild } from '@angular/core';
import { HmfModalComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconSignCancel
} from '@dsg-tech/homefield-angular-icons';
import { filter, interval, map, Subject, take } from 'rxjs';

import { ReviewSectionComponent } from '../review-section/review-section.component';
import { Store } from '@ngrx/store';
import { closeReviewImageModal } from '@product-reviews/data-access';

@Component({
	selector: 'product-reviews-modal',
	templateUrl: './review-modal.component.html',
	styleUrl: './review-modal.component.scss',
	imports: [CommonModule, HomefieldIconLibraryModule, ReviewSectionComponent]
})
export class ReviewModalComponent implements OnDestroy {
	homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	private readonly document = inject(DOCUMENT);
	private readonly store = inject(Store);

	private previousFocusedElement: Element;
	private UNSUBSCRIBE$ = new Subject<void>();

	private readonly modalFactoryService = inject(HmfModalFactoryService);
	public modalRef: ComponentRef<HmfModalComponent>;
	readonly reviewModalTemplateRef =
		viewChild.required<TemplateRef<unknown>>('reviewModalTemplate');

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconSignCancel]);
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	openModal() {
		this.previousFocusedElement = this.document.activeElement;
		if (!this.modalRef) {
			this.modalRef = this.modalFactoryService.create(
				HmfModalComponent,
				{
					className: 'review-modal',
					transparentBackground: false,
					focus: true,
					interactability: false,
					isCloseButtonSticky: true
				},
				this.reviewModalTemplateRef()
			);
			this.modalRef.instance.closed.subscribe(() => this.closeModalEvt());
		}

		this.modalRef.instance?.openModal();
		interval(300)
			.pipe(
				map(() => {
					if (this.modalRef?.instance.modalContainer) {
						this.handleFocusFirstElement();
					}

					return !!this.modalRef?.instance.modalContainer;
				}),
				filter(Boolean),
				take(1)
			)
			.subscribe();
	}

	closeModalEvt() {
		this.store.dispatch(closeReviewImageModal());
		this.restoreFocus();
	}

	onKeydownEvent(event: KeyboardEvent) {
		const closeButton = this.document.getElementsByClassName('hmf-modal-container-close');

		if (
			event.key === 'Enter' &&
			closeButton &&
			Array.from(closeButton).find((els) => els == this.document.activeElement)
		) {
			this.modalRef.instance?.closeModal();
		}
	}

	//TODO: workaound to focus previous element => fix in homefield and remove
	restoreFocus() {
		(this.previousFocusedElement as HTMLElement).focus();
	}

	//TODO: fix in homefield and remove
	handleFocusFirstElement() {
		const focusElement =
			this.modalRef?.instance?.modalContainer?.nativeElement?.querySelectorAll(
				'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
			)[0] as HTMLElement;

		if (focusElement) {
			const x = window.scrollX;
			const y = window.scrollY;
			focusElement.focus();
			window.scrollTo(x, y);
		}
	}
}
